import React, { useEffect, useState } from 'react';
import '../CSS/ReportsGrid.css';

const UmbrellaReportsGrid = ({
  searchTerm,
  selectedSector,
  groupResponsible,
  targetedEntity,
  selectedDropdownFilter,
  compName,
  startDate,
  endDate,
  selectedIndustryFilter,
  selectedRegion
}) => {
  const [reports, setReports] = useState([]);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    fetch('/api/hello')
      .then((response) => response.json())
      .then((data) => {
        const baseFilteredReports = data.reports.filter(
          (report) => !compName || report.fields.SpecCompanyHidden === compName
        );

        setReports(baseFilteredReports);
        setOrgs(data.orgs || []);
      })
      .catch((error) => {
        console.error('Error fetching reports data:', error);
      });
  }, [compName]);

  const filterReports = (
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    orgs,
    compName
  ) => {
    const groupResponsibleLower = groupResponsible?.toLowerCase();
    let relatedGroupNames = new Set();

    if (groupResponsibleLower && orgs.length > 0) {
      orgs.forEach((org) => {
        const title = org.fields?.Title?.toLowerCase();
        const aka = org.fields?.AKA?.toLowerCase();
        const oldName = org.fields?.OldName?.toLowerCase();

        if (
          title?.includes(groupResponsibleLower) ||
          aka?.includes(groupResponsibleLower) ||
          oldName?.includes(groupResponsibleLower)
        ) {
          if (title) relatedGroupNames.add(title);
          if (aka) relatedGroupNames.add(aka);
          if (oldName) relatedGroupNames.add(oldName);
        }
      });

      if (relatedGroupNames.size === 0) {
        relatedGroupNames.add(groupResponsibleLower);
      }
    }

    return (reports || []).filter((report) => {
      if (!report.fields) return false;

      const companyMatches = !compName || report.fields.SpecCompanyHidden === compName;
      const sector = report.fields.field_6 || [];
      const searchTermLower = searchTerm?.toLowerCase();

      const titleMatches =
        !searchTerm || report.fields.Title?.toLowerCase().includes(searchTermLower);

      const groupResponsibleMatches =
        !groupResponsible ||
        [...relatedGroupNames].some(
          (name) =>
            report.fields.field_10?.toLowerCase().includes(name) ||
            report.fields.GroupResponsibleLU?.some((item) =>
              item.LookupValue?.toLowerCase().includes(name)
            )
        );

      const targetedEntityMatches =
        !targetedEntity ||
        (report.fields.field_11 &&
          report.fields.field_11.toLowerCase().includes(targetedEntity.toLowerCase()));

      const dropdownFilterMatches =
        !selectedDropdownFilter ||
        (report.fields.field_9 &&
          report.fields.field_9.toLowerCase().includes(selectedDropdownFilter.toLowerCase()));

      const regionMatches =
        !selectedRegion ||
        (report.fields.Region0 && report.fields.Region0 === selectedRegion);

      const reportDate = new Date(report.fields.field_1_formatted);
      const startDateMatches = !startDate || reportDate >= new Date(startDate);
      const endDateMatches = !endDate || reportDate <= new Date(endDate);

      const industryMatches =
        !selectedIndustryFilter ||
        (report.fields.ARIndustry &&
          report.fields.ARIndustry.toLowerCase().includes(selectedIndustryFilter.toLowerCase())) ||
        (report.fields.ESIndustry &&
          report.fields.ESIndustry.toLowerCase().includes(selectedIndustryFilter.toLowerCase())) ||
        (report.fields.PharmaIndustry &&
          report.fields.PharmaIndustry.toLowerCase().includes(selectedIndustryFilter.toLowerCase()));

      if (
        !selectedSector &&
        !searchTerm &&
        !groupResponsible &&
        !targetedEntity &&
        !selectedDropdownFilter &&
        !selectedRegion
      ) {
        return companyMatches && startDateMatches && endDateMatches && industryMatches;
      }

      if (selectedSector) {
        const sectorMatches = sector.some((sec) =>
          sec.toLowerCase().includes(selectedSector.toLowerCase())
        );
        return (
          companyMatches &&
          titleMatches &&
          sectorMatches &&
          groupResponsibleMatches &&
          targetedEntityMatches &&
          dropdownFilterMatches &&
          regionMatches &&
          startDateMatches &&
          endDateMatches &&
          industryMatches
        );
      }

      return (
        companyMatches &&
        titleMatches &&
        groupResponsibleMatches &&
        targetedEntityMatches &&
        dropdownFilterMatches &&
        regionMatches &&
        startDateMatches &&
        endDateMatches &&
        industryMatches
      );
    });
  };

  const filteredReports = filterReports(
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    orgs,
    compName
  );

  // replace your openReport with this:
const openReport = (linkUrl, reportTitle, reportType = 'Umbrella') => {
  const userEmail = localStorage.getItem('user-email');
  if (!userEmail) {
    alert('You must be logged in to view this report.');
    return;
  }

  // pull last path segment and strip any querystring
  const lastPart = (linkUrl || '').split('/').pop() || '';
  const decodedFile = decodeURIComponent(lastPart.split('?')[0]); // e.g. "My Report 2025.pdf"
  const encodedForPath = encodeURIComponent(decodedFile);         // safe for /api path

  // what to log: prefer the real title from SharePoint; else a clean name without .pdf
  const reportNameToLog = (reportTitle || decodedFile).replace(/\.pdf$/i, '');

  // 🔸 keep your current URL shape; if your server no longer needs ?email, you can drop it
  const reportUrl = `/api/reports/${encodedForPath}?email=${encodeURIComponent(userEmail)}`;

  // fire-and-forget log (human-readable name, no %20)
  fetch('/api/log-report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      reportName: reportNameToLog,
      reportType,
      userEmail
    })
  }).catch(() => {});

  window.open(reportUrl, '_blank', 'noopener,noreferrer');
};


  return (
    <div className="reports-grid">
      <div className="reports-header">
        <div className="header-item">Title</div>
        <div className="header-item">Report Date</div>
        <div className="header-item">Incident Date</div>
        <div className="header-item">Country</div>
        <div className="header-item">Sector</div>
      </div>
      {filteredReports.map((report) => (
        <div className="report-row" key={report.fields.id}>
          {report.fields.Links1 ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                openReport(report.fields.Links1);
              }}
              className="report-item report-title"
              data-label="Title"
            >
              {report.fields.Title}
            </a>
          ) : (
            <div className="report-item">{report.fields.Title}</div>
          )}
          <div className="report-item" data-label="Report Date">
            {report.fields.field_1_formatted}
          </div>
          <div className="report-item" data-label="Incident Date">
            {report.fields.field_2_formatted}
          </div>
          <div className="report-item" data-label="Country">
            {report.fields.field_3}
          </div>
          <div className="report-item" data-label="Sector">
          <div className="report-item">
              {Array.isArray(report.fields.field_6)
                ? report.fields.field_6.join(', ')
                : report.fields.field_6 || ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UmbrellaReportsGrid;
